"use client";

import Link from "next/link";
import { useState } from "react";
import { ArrowUpRight, DatabaseZap } from "lucide-react";
import type { PriceRow } from "@/lib/api";
import { formatDateTr } from "@/lib/date-format";
import { productHref } from "@/lib/product-links";
import ProductImage from "@/components/ui/ProductImage";
import { sortProductRows } from "./market-explorer-utils";

function money(value: number | string | null) {
  if (value == null || value === "") return "—";
  const number = typeof value === "number" ? value : Number(value);
  return Number.isFinite(number)
    ? number.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "—";
}

function priceText(row: PriceRow) {
  const min = Number(row.minPrice);
  const max = Number(row.maxPrice);
  if (min > 0 && max > 0) return `${money(row.minPrice)} – ${money(row.maxPrice)}`;
  if (Number(row.avgPrice) > 0) return money(row.avgPrice);
  return min > 0 ? money(row.minPrice) : max > 0 ? money(row.maxPrice) : "—";
}

export default function MarketProductTable({ rows, loading, marketName, sourceKind = "hal" }: { rows: PriceRow[]; loading: boolean; marketName: string; sourceKind?: "hal" | "borsa" | "aggregate" }) {
  const products = sortProductRows(rows);
  const [expanded, setExpanded] = useState(false);
  const visibleProducts = expanded ? products : products.slice(0, 6);

  if (loading && products.length === 0) {
    return <div className="divide-y divide-[#d9dfdb] border-y border-[#a9b6ae]" aria-live="polite">
      {Array.from({ length: 6 }, (_, index) => <div key={index} className="h-[74px] animate-pulse bg-[linear-gradient(90deg,transparent,#f0f3f1,transparent)]" />)}
    </div>;
  }

  if (products.length === 0) {
    return <div className="border-y border-[#a9b6ae] py-16 text-center">
      <DatabaseZap className="mx-auto size-7 text-[#68736d]" aria-hidden />
      <h2 className="type-h2 mt-4">{sourceKind === "aggregate" ? "Bu kaynak" : sourceKind === "borsa" ? "Bu borsa" : "Bu hal"} için yayımlanmış ürün yok</h2>
      <p className="mt-2 text-sm text-[#68736d]">Kaynak yeni bülten yayımladığında fiyatlar burada otomatik görünür.</p>
    </div>;
  }

  return <div className="border-y border-[#a9b6ae]">
    <div className="hidden grid-cols-[minmax(240px,1.35fr)_minmax(170px,.8fr)_90px_140px_32px] gap-5 border-b border-[#a9b6ae] py-3 text-eyebrow font-semibold uppercase tracking-[0.08em] text-[#68736d] md:grid">
      <span>Ürün</span><span>Fiyat / aralık (TL)</span><span>Birim</span><span>Kayıt tarihi</span><span />
    </div>
    <div className="divide-y divide-[#d9dfdb]" aria-busy={loading} aria-label={`${marketName} ürün fiyatları`}>
      {visibleProducts.map((row, index) => <Link key={`${row.marketSlug}-${row.productSlug}`} href={productHref(row)} className="group grid min-h-[72px] grid-cols-[minmax(0,1fr)_auto] items-center gap-4 py-3 transition-colors hover:bg-[#f4f6f4] md:grid-cols-[minmax(240px,1.35fr)_minmax(170px,.8fr)_90px_140px_32px] md:gap-5 md:px-2">
        <span className="flex min-w-0 items-center gap-3">
          <ProductImage slug={row.productSlug} canonicalSlug={row.canonicalProduct} name={row.productName} categorySlug={row.categorySlug} imageUrl={row.imageUrl} size={48} priority={index < 6} className="!rounded-none !border-0 !shadow-none" />
          <strong className="truncate text-[15px] text-[#101412]">{row.productName}</strong>
        </span>
        <span className="text-right md:text-left">
          <strong className="block font-(family-name:--font-mono) text-label text-[#101412]">{priceText(row)}</strong>
          <small className="mt-1 block text-eyebrow text-[#68736d] md:hidden">{row.unit} · {formatDateTr(row.recordedDate)}</small>
        </span>
        <span className="hidden text-data text-[#4f5a54] md:block">{row.unit}</span>
        <time className="hidden text-data text-[#4f5a54] md:block" dateTime={row.recordedDate.slice(0, 10)}>{formatDateTr(row.recordedDate)}</time>
        <ArrowUpRight className="hidden size-4 text-[#68736d] transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 md:block" aria-hidden />
      </Link>)}
    </div>
    {products.length > 6 ? <button type="button" onClick={() => setExpanded((value) => !value)} className="min-h-12 w-full border-t border-[#d9dfdb] text-data font-bold text-[#101412] transition-colors hover:bg-[#f4f6f4]">{expanded ? "Listeyi kısalt" : `Tüm ${products.length} ürünü göster`}</button> : null}
  </div>;
}
